import { useCallback, useEffect, useMemo, useRef } from "react";

import type { DateValueType } from "../../../DatepickerV2";
import type { DropdownOption } from "../../../DropDownV2";
import type {
  ClientsPageAnalytics,
  ReturnedTranslatedContentClientsPageHook,
} from "../../types";
import type { ClientANFTableFilterSubProps } from "./types";

import { useIsMobile } from "../../../hooks";
import { useDebounceFn } from "../../../utils/debounce";
import { formatClientTrackingDate } from "../../utils";
import { useClientApprovedNotFundedContext } from "../providers/ApprovedNotFundedProvider";
import { DesktopFilters } from "./DesktopFilters";
import { MobileFilters } from "./MobileFilters";
import { MobileFiltersV2 } from "./MobileFiltersV2";

export type ClientANFReportFilterProps = {
  analytics?: ClientsPageAnalytics;
  translations: ReturnedTranslatedContentClientsPageHook;
  isMobileBottomSheetEnable?: boolean;
};

export const ClientApprovedNotFundedFilters = ({
  analytics,
  translations,
  isMobileBottomSheetEnable,
}: ClientANFReportFilterProps) => {
  const isMobile = useIsMobile();

  const {
    state: { dateRange, selected },
    options: { rebateAccountIDs: rebateAccountOptions },
    actions,
    runResource,
  } = useClientApprovedNotFundedContext();
  const rebateAccountIDsRef = useRef<DropdownOption[]>(rebateAccountOptions);

  useEffect(() => {
    // only set selected rebate accounts on first load if none selected
    if (
      rebateAccountIDsRef.current &&
      rebateAccountIDsRef.current.length === 0 &&
      rebateAccountOptions.length > 0
    ) {
      actions.setSelected(
        "rebateAccountIDs",
        rebateAccountOptions.map((o) => o.value),
      );
    }
  }, [actions, rebateAccountOptions]);

  const [handleSearchInput] = useDebounceFn((value: string) => {
    actions.setSearchQuery(value);
    analytics?.trackTableSearchFieldChangeEvent("Approved not funded", value);
  }, 1000);

  const handleDateRangePicker = useCallback(
    (value: DateValueType) => {
      if (
        formatClientTrackingDate(value?.startDate) ===
          formatClientTrackingDate(dateRange.from) ||
        formatClientTrackingDate(value?.endDate) ===
          formatClientTrackingDate(dateRange.to)
      ) {
        analytics?.trackDateRangeFilterEvent(
          formatClientTrackingDate(value?.startDate),
          formatClientTrackingDate(value?.endDate),
          "Approved not funded",
        );
      }

      actions.setDateRange({
        from: value?.startDate ?? null,
        to: value?.endDate ?? null,
      });
    },
    [actions, analytics, dateRange.from, dateRange.to],
  );

  const handleRebateAccountsDropdown = useCallback(
    (options: DropdownOption[]) => {
      const newRebateAccountIDs = options.map((o) => o.value);
      if (
        JSON.stringify(newRebateAccountIDs) !==
        JSON.stringify(selected.rebateAccountIDs)
      ) {
        analytics?.trackTableRebateSelectEventV2(
          "Approved not funded",
          newRebateAccountIDs,
        );
      }

      actions.setSelected("rebateAccountIDs", newRebateAccountIDs);
    },
    [actions, analytics, selected.rebateAccountIDs],
  );

  const [debounceDateTooltipTrack] = useDebounceFn((isMobile: boolean) => {
    analytics?.trackClientDateTooltipEvent(isMobile, "Approved not funded");
  }, 5000);

  const filterConfig = useMemo<ClientANFTableFilterSubProps>(
    () => ({
      handleDateRangePicker,
      handleExportButton: () => runResource("csv"),
      handleRebateAccountsDropdown,
      handleSearchInput,
      translations,
      onDateTooltipHover: debounceDateTooltipTrack,
    }),
    [
      handleDateRangePicker,
      handleRebateAccountsDropdown,
      handleSearchInput,
      translations,
      debounceDateTooltipTrack,
      runResource,
    ],
  );

  if (isMobile && isMobileBottomSheetEnable) {
    return <MobileFiltersV2 handleSearchInput={handleSearchInput} />;
  }

  return isMobile ? (
    <MobileFilters {...filterConfig} />
  ) : (
    <DesktopFilters {...filterConfig} />
  );
};
